import { ChangeEvent, useState } from "react";
import { useAlertContext, useUserContext } from "../../contexts";
import { AlertType, ApplicationScreen, Clinic, Doctor } from "../../models";
import { apiAccessRequest } from "../../constants";
import { ConfirmLogoutModal } from "./ConfirmLogoutModal";
import Switch from "@mui/material/Switch";
import { BuildUrl } from "../../helpers";
import { useMsal } from "@azure/msal-react";
import CircularProgress from "@mui/material/CircularProgress";
import { useCreateAuditLog } from "../../hooks";

interface Props {
	doctors: Doctor[];
	clinic?: Clinic;
	setRefreshDoctors: React.Dispatch<React.SetStateAction<boolean>>;
}

type DoctorActionMessages = {
	success: string;
	error: string;
	auditLogAction: string;
};

export const DoctorTable = ({ doctors, clinic, setRefreshDoctors }: Props) => {
	const { account } = useUserContext();
	const { instance } = useMsal();
	const { showAlert } = useAlertContext();
	const { createAuditLog } = useCreateAuditLog();

	const [openConfirmLogoutModal, setOpenConfirmLogoutModal] = useState(false);
	const [selectedDoctor, setSelectedDoctor] = useState<Doctor | undefined>(
		undefined
	);
	const [isLoading, setIsLoading] = useState(false);

	const logoutDoctor = async (doctor: Doctor) => {
		setSelectedDoctor(doctor);
		setOpenConfirmLogoutModal(true);
	};
	const closeConfirmLogoutModal = (updated: boolean) => {
		setSelectedDoctor(undefined);
		setOpenConfirmLogoutModal(false);
		if (updated) {
			setRefreshDoctors(true);
		}
	};

	const toggleDoctorTakingPatients = async (
		event: ChangeEvent<HTMLInputElement>,
		checked: boolean
	) => {
		const doctorId = event.target.id;
		if (!doctorId || Number(doctorId) <= 0) {
			return;
		}
		const doctor = doctors.find((doctor) => doctor.id === Number(doctorId));
		if (!doctor || !doctor.available) {
			return;
		}

		await toggleTakingPatients(doctor, checked);
	};

	const getAuthenticatedHeaders = async () => {
		const tokenResponse = await instance.acquireTokenSilent({
			account,
			scopes: apiAccessRequest.scopes
		});

		return {
			Authorization: `Bearer ${tokenResponse.accessToken}`,
			"Content-Type": "application/json"
		};
	};

	const postDoctorAction = async (
		doctor: Doctor,
		requestUrl: string,
		messages: DoctorActionMessages,
		body?: object
	) => {
		setIsLoading(true);
		try {
			const response = await fetch(requestUrl, {
				method: "POST",
				headers: await getAuthenticatedHeaders(),
				body: body ? JSON.stringify(body) : undefined
			});

			if (!response.ok) {
				throw new Error(messages.error);
			}

			void createAuditEntry(doctor, messages.auditLogAction);

			showAlert({
				message: messages.success,
				alertType: AlertType.success,
				timeout: true
			});
			setRefreshDoctors(true);
		} catch (error) {
			showAlert({
				message: messages.error,
				alertType: AlertType.error,
				timeout: true
			});
		} finally {
			setIsLoading(false);
		}
	};

	const toggleTakingPatients = async (
		doctor: Doctor,
		shouldTakePatients: boolean
	) => {
		const requestUrl = shouldTakePatients
			? BuildUrl(`doctor/${doctor.id}/StartTakingPatients`)
			: BuildUrl(`doctor/${doctor.id}/StopTakingPatients`);

		await postDoctorAction(doctor, requestUrl, {
			success: "The doctor's availability has been updated",
			error: "There was an error updating the doctor's availability",
			auditLogAction: shouldTakePatients
				? "Doctor resumed taking patients"
				: "Doctor stopped taking patients"
		});
	};

	const increaseTakingPatients = async (doctor: Doctor) => {
		await postDoctorAction(
			doctor,
			BuildUrl(`doctor/${doctor.id}/taking-patients-limit/increment`),
			{
				success: "The doctor's stop taking patient limit has been updated",
				error:
					"There was an error updating the doctor's stop taking patient limit",
				auditLogAction: "Increase stop taking patients limit"
			},
			{ dailyLimit: 1 }
		);
	};

	const createAuditEntry = async (doctor: Doctor, action: string) => {
		try {
			await createAuditLog([
				{
					doctorId: doctor.id,
					doctorName: doctor.fullName,
					clinicId: clinic?.id,
					username: account?.name ?? "",
					applicationScreen: ApplicationScreen.AdminCentreOperations,
					action
				}
			]);
		} catch (error) {
			console.log("Error creating audit log", error);
		}
	};

	const renderAvailability = (doctor: Doctor) => (
		<div
			className={`ch-chip ${doctor.available ? "ch-chip-green" : "ch-chip-red"}`}
		>
			{doctor.available ? "Available" : "Unavailable"}
		</div>
	);

	if (!isLoading) {
		return (
			<>
				<table className="ch-table w-full">
					<thead>
						<tr>
							<th>Order</th>
							<th>Doctor</th>
							<th>Gender</th>
							<th>Availability</th>
							<th>Taking Patients</th>
							<th>Stop Taking Patients Count</th>
							<th>Increase Taking Patients Limit</th>
							<th>Logout Doctor</th>
						</tr>
					</thead>
					<tbody>
						{doctors?.map((doctor, index) => (
							<tr key={doctor.id}>
								<td>
									{doctor.available && doctor.takingPatients ? index + 1 : ""}
								</td>
								<td>{`${doctor.salutation} ${doctor.fullName}`}</td>
								<td>{doctor.sex}</td>
								<td>{renderAvailability(doctor)}</td>
								<td align="center">
									<Switch
										id={doctor.id.toString()}
										checked={doctor.takingPatients && doctor.available}
										onChange={toggleDoctorTakingPatients}
										name={doctor.displayName}
									/>
								</td>
								<td align="center">{`${doctor.stopTakingPatientCount} of ${doctor.dailyLimit} used`}</td>
								<td>
									<button
										className="ch-button ch-button-small ch-button-clear"
										onClick={() => increaseTakingPatients(doctor)}
									>
										Add 1
									</button>
								</td>
								<td>
									{doctor.available && (
										<button
											className="ch-button ch-button-small  ch-button-clear"
											onClick={() => logoutDoctor(doctor)}
										>
											<img
												src="images/svg/settings-toggle-horizontal.svg"
												alt="logout doctor"
												className="mr-2"
											/>
											Logout
										</button>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>

				<ConfirmLogoutModal
					open={openConfirmLogoutModal}
					doctor={selectedDoctor}
					clinic={clinic}
					close={closeConfirmLogoutModal}
				/>
			</>
		);
	} else {
		return (
			<div className="flex justify-center py-3">
				<CircularProgress size="80px" />
			</div>
		);
	}
};
